/* eslint-disable no-console */
import {useQueryClient} from '@tanstack/react-query';
import {
	AuthorizationsEnum,
	ExamBookingEnum,
	FeaturesEnum,
	QueryParamsEnum,
} from 'presentation/constants';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams} from 'presentation/pages';
import {BookingTypeEnum, FlowEnum} from 'presentation/pages/private/booking/constants';

import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {useBookingAuthorization} from 'presentation/modules/booking/shared/hooks/useBookingAuthorization';

import {
	CheckBookingModal,
	CheckBookingModalEnum,
} from 'presentation/pages/private/booking/hooks/useBookingSpecialty/types';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {ParsedExamAppointment} from 'presentation/pages/private/home/utils/parseExamAppointments/types';
import {translations} from 'presentation/translations-new';
import {ExamSpecialty, Specialty} from 'presentation/utils';
import {getSpecialtyBySearch} from 'presentation/utils/GetSpecialtyBySearch';
import {textHandle} from 'presentation/utils/textHandle';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useExamOrientationsInfoStore} from '../../components/ExamOrientationsDialog/store/useExamOrientationsInfoStore';
import {ExamInfo} from '../../components/ExamOrientationsDialog/store/useExamOrientationsInfoStore/types';
import {useExamOrientationsModalStore} from '../../components/ExamOrientationsDialog/store/useExamOrientationsModalStore';
import {useExamList} from '../useExamList';
import {UseExamSelectionProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {examsPreparationQueryOptions} from 'presentation/modules/myAppointments/ui/components/PrepareModalContent/queries/useGetExamsPreparationQuery';
import {useAccountInformation} from 'presentation/pages/private';

const {verifyingAuth} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

const {loading} = translations['pt-br'].commons;

export function useExamSelection({handleNextStep}: UseExamSelectionProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {
		clearSearch,
		setSearchParams,
		SEARCH_PARAMS: {query, searchByLetter},
	} = useQueryParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		beneficiary: {idadeC},
	} = useAccountInformation();
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const [checkBookingModal, setCheckBookingModal] = useState<CheckBookingModal>('none');
	const [scheduledBooking, setScheduledBooking] = useState<ParsedExamAppointment | null>(null);
	const examOrientationsModal = useExamOrientationsModalStore(state => state.isOpen);
	const setExamOrientationsModal = useExamOrientationsModalStore(state => state.setIsOpen);
	const setExamOrientationsInfo = useExamOrientationsInfoStore(
		state => state.setExamOrientationInfo,
	);
	const resetExamOrientationInfo = useExamOrientationsInfoStore(
		state => state.resetExamOrientationInfo,
	);
	const hasExamPreparation = useExamOrientationsInfoStore(
		state => !!state.examOrientationInfo.orientations,
	);
	const {verifyExamAuthorization} = useBookingAuthorization();
	const {refetch: refetchAppointmentsList} = useGetHomeQuery();
	const [isSelectedExamAuthRequired, setIsSelectedExamAuthRequired] = useState(false);
	const [examSelected, setExamSelected] = useState('');
	const [isOnDemandExamDrawerOpen, setIsOnDemandExamDrawerOpen] = useState(false);

	const {isEnabled: isExamsSpecialtyProcedureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_EXAMS_SPECIALTY_PROCEDURE,
	);
	const {isEnabled: isSemanticSearchEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SEMANTIC_SEARCH_MEDICAL_GUIDE,
	);
	const {isEnabled: isOnDemandExamsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ON_DEMAND_EXAMS,
	);

	const {
		examsList,
		examsListError,
		isFetchingExamsList,
		refetchExamsList,
		examListHasUnprocessableError,
	} = useExamList();

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	const showNotFoundState = !examsList || examsList.length === 0 || examListHasUnprocessableError;

	const handleNavToStateSelection = () => {
		clearSearch();
		resetBookingData();
		navigate(ExamBookingEnum.EXAM_BOOKING_UF);
	};

	const searchTerm = textHandle(query).sanitizeText();
	const searchFilteredByTerms = getSpecialtyBySearch({text: searchTerm, flowType: FlowEnum.EXAM});

	const FILTERED_LIST_BY_CATEGORY = examsList?.map(specialty => ({
		...specialty,
		list: specialty?.list?.filter(item => {
			const formattedText = textHandle(item?.formattedText).sanitizeText();
			const termsRegex = new RegExp(`\\b${searchFilteredByTerms}\\b`, 'i');

			return formattedText.includes(searchTerm) || termsRegex.test(formattedText);
		}),
	}));

	const FILTERED_LIST_BY_LETTER = FILTERED_LIST_BY_CATEGORY?.filter(
		item => item?.category === searchByLetter,
	);

	const LIST = searchByLetter ? FILTERED_LIST_BY_LETTER : FILTERED_LIST_BY_CATEGORY;

	const showEmptyState = LIST?.every(categoryItem => categoryItem?.list?.length === 0);

	const handleNavigateToExamAuth = () => {
		navigate(AuthorizationsEnum.AUTHORIZATIONS);
	};

	const handleCloseExamAuthRequiredDrawer = () => {
		setIsSelectedExamAuthRequired(false);
		setExamSelected('');
	};

	// 204 No Content returns an empty body, so there is no dsDescricao
	const fetchOnDemandExamPreparation = async (cdTipoExame: string) => {
		try {
			const examPreparation = await queryClient.fetchQuery(
				examsPreparationQueryOptions({cdTipoExame, nuIdade: String(idadeC)}),
			);
			return examPreparation?.dsDescricao ?? '';
		} catch {
			return '';
		}
	};

	const handleSelectCardExam = async (specialty: Specialty) => {
		setExamSelected(specialty.formattedText);
		try {
			if ((specialty as ExamSpecialty).flAutorizacao) {
				setCheckBookingModal('verifyingAuth');
				const {autorizaAgendamento} = await verifyExamAuthorization({
					cdTipoExame: Number(specialty.code),
					cdPontoEntrada: Number(EntryPointCodeEnum.V1),
				});
				setCheckBookingModal('none');

				if (!autorizaAgendamento) {
					setIsSelectedExamAuthRequired(true);

					return;
				}
			}

			const isOnDemandExam = isOnDemandExamsEnabled && specialty.flLivreDemanda;
			let orientations = (specialty as ExamSpecialty).dsPreparo;

			if (isOnDemandExam) {
				setCheckBookingModal('verifyingDuplicity');
				orientations = await fetchOnDemandExamPreparation(specialty.code);
				setCheckBookingModal('none');
			}

			setExamOrientationsInfo({
				code: specialty.code,
				examName: specialty.formattedText,
				orientations,
				procedureCode: (specialty as ExamSpecialty).cdProcedimento,
			});

			if (isOnDemandExam) {
				setIsOnDemandExamDrawerOpen(true);
				return;
			}

			setExamOrientationsModal(true);
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	};

	const handleOpenOnDemandExamDrawer = () => {
		setIsOnDemandExamDrawerOpen(true);
	};

	const handleCloseOnDemandExamDrawer = () => {
		setIsOnDemandExamDrawerOpen(false);
		resetExamOrientationInfo();
	};

	const handleViewOnDemandExamUnits = () => {
		setIsOnDemandExamDrawerOpen(false);
		setExamOrientationsModal(true);
	};

	const resetBookingDataSpecialties = () => {
		setBookingData(prev => ({
			...prev,
			specialtyCode: '',
			specialtyName: '',
			subSpecialtyCode: undefined,
		}));
	};

	const clearExamSelection = () => {
		clearSearch();
		resetBookingDataSpecialties();
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SPECIALTY_NAME);
				prev.delete(QueryParamsEnum.PROCEDURE);
				return prev;
			},
			{replace: true},
		);
	};

	const handleAgreeExamOrientations = async (examInfo: ExamInfo) => {
		setExamOrientationsModal(false);

		try {
			setCheckBookingModal('verifyingDuplicity');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList({
				throwOnError: true,
			});
			const alreadyScheduled = updatedAppointmentsList?.appointments?.find(
				booking =>
					booking.specialtyCode === examInfo.code &&
					booking.bookingType === BookingTypeEnum.EXAM,
			);

			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled as ParsedExamAppointment);
				setCheckBookingModal('duplicityState');
				return;
			}

			setCheckBookingModal('none');
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
			return;
		}

		setBookingData(prev => ({
			...prev,
			specialtyCode: examInfo.code,
			specialtyName: examInfo.examName,
			procedureCode: examInfo.procedureCode,
			professionalCode: '',
			professionalName: '',
			unitCode: prev.isAdvanceDiagnosticsFlow ? prev.unitCode : '',
			unitName: prev.isAdvanceDiagnosticsFlow ? prev.unitName : '',
			unitAddress: prev.isAdvanceDiagnosticsFlow ? prev.unitAddress : '',
			dateAt: '',
			hourAt: '',
		}));

		clearSearch();
		handleNextStep();
	};

	return {
		loadingText,
		isFetching: isFetchingExamsList,
		showNotFoundState,
		handleNavToStateSelection,
		LIST,
		showEmptyState,
		handleSelectCardExam,
		checkBookingModal,
		scheduledBooking,
		resetBookingDataSpecialties,
		handleAgreeExamOrientations,
		examOrientationsModal,
		setExamOrientationsModal,
		refetch: refetchExamsList,
		examsListError,
		setCheckBookingModal,
		examListHasUnprocessableError,
		isSelectedExamAuthRequired,
		handleNavigateToExamAuth,
		examSelected,
		handleCloseExamAuthRequiredDrawer,
		query,
		isExamsSpecialtyProcedureFlagEnabled,
		isSemanticSearchEnabled,
		clearExamSelection,
		bookingData,
		isOnDemandExamsEnabled,
		isOnDemandExamDrawerOpen,
		hasExamPreparation,
		handleOpenOnDemandExamDrawer,
		handleCloseOnDemandExamDrawer,
		handleViewOnDemandExamUnits,
	};
}
